/**
 * Zwraca wartość parametru z URL
 *
 * @param {string} url - adres url
 * @param {string} searchedParam - nazwa szukanego parametru
 * @returns {string | null} wartość parametru
 */
export const getParamURL = (url: string, searchedParam: string): string | null => {
    const urlParams = new URLSearchParams(new URL(url).search);
    return urlParams.get(searchedParam);
};

/**
 * Zwraca wartość hasha z URL - ogranicza długość do 24 znaków.
 *
 * @param {string} url - adres URL
 * @param {string} searchedHash - nazwa szukanego hasha
 * @returns {string | null} ID testu
 */
export function getHashValue(url: string, searchedHash: string): string | null {
    if (!searchedHash) return null;

    const addressURL = new URL(url);
    const hashParams = addressURL.hash;
    let hashValue = null;

    if (hashParams.indexOf(`#${searchedHash}`) !== -1) {
        hashValue = hashParams.split(searchedHash)[1]?.substring(0, 24);
    }

    return hashValue;
}

/**
 * Wyrzuca wszystkie "białe znaki" z ciągu znaków
 *
 * @param {string} value - ciąg znaków do sprawdzenia
 * @returns {string} przekazany ciąg znaków z usuniętymi 'spacjami'
 */
export const removeWhitespace = (value: string): string => {
    return value.replace(/\s/g, '');
};

/**
 * Konwertuje wszystkie elementy tablicy na liczby.
 * W przypadku wartości, które nie mogą być sparsowane na liczby zwraca NaN.
 *
 * @param {any[]} items - tablica elementów
 * @returns {(number | number[])[]} tablica z liczbami
 */
export const parseIntArr = (items: (string | number | (string | number)[])[]): number[] => {
    return items.map((item) => {
        if (Array.isArray(item)) {
            return parseIntArr(item);
        }
        if (typeof item !== 'number') {
            return parseInt(String(item), 10);
        }
        return item;
    }) as number[];
};

/**
 * Konwertuje wszystkie klucze obiektu na wielkie litery i usuwa spacje w wartościach typu string.
 *
 * @param {Record<string, unknown>} obj - obiekt z danymi
 * @returns {Record<string, unknown>} obiekt z ustawionymi kluczami
 */
export const uppercaseObjectKeys = (obj: Record<string, unknown>): Record<string, unknown> => {
    const newObj: Record<string, unknown> = {};
    if (isObjectType(obj)) {
        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (isStringType(value)) {
                newObj[key.toUpperCase()] = value.replace(/\s/g, '');
            } else {
                newObj[key.toUpperCase()] = value;
            }
        });
    }
    return newObj;
};

/**
 * Funkcja sprawdza, czy przekazana wartość jest typu string.
 *
 * @param {unknown} el - element do sprawdzenia
 * @returns {el is string} zwraca true, jeżeli sprawdzany element jest stringiem i określa typ.
 */
export const isStringType = (el: unknown): el is string => {
    return typeof el === 'string';
};

/**
 * Funkcja sprawdza, czy przekazany element jest typu array.
 *
 * @param {unknown} el - wartość do sprawdzenia
 * @returns {el is Array} zwraca true, jeżeli sprawdzany element jest tablicą i określa, że element jest typu Array.
 */
export const isArrayType = (el: unknown): el is Array<any> => {
    return Array.isArray(el);
};

/**
 * Funkcja sprawdza, czy przekazany element jest zdefiniowany
 *
 * @param {unknown} el - element do sprawdzenia
 * @returns {el} zwraca informację o elemencie
 */
export const isDefined = (el: unknown): boolean => {
    return typeof el !== 'undefined';
};

/**
 * Funkcja sprawdza, czy przekazany element pusty.
 *
 * @param {unknown} el - element do sprawdzenia
 * @returns {el} zwraca informację o elemencie
 */
export const isEmpty = (el: unknown): boolean => {
    if (isStringType(el) || isArrayType(el)) {
        return !el || el.length === 0;
    } else if (isObjectType(el)) {
        for (const prop in el) {
            if (Object.hasOwn(el, prop)) {
                return false;
            }
        }

        return JSON.stringify(el) === JSON.stringify({});
    }
    return false;
};

/**
 * Funkcja sprawdza, czy przekazany element jest obiektem i czy wartość nie jest null.
 *
 * @param {unknown} el - element do sprawdzenia
 * @returns {el is Record<string, unknown>} zwraca true, jeżeli sprawdzany element jest obiektem i określa typ.
 */
export const isObjectType = (el: unknown): el is Record<string, unknown> => {
    return typeof el === 'object' && el !== null && !isArrayType(el);
};

/**
 * Zwraca elementy tablicy, które mają ustawioną wskazaną właściwość
 *
 * @param {T[]} arr - tablica elementów do przefiltrowania
 * @param {keyof T} prop - klucz/właściwość jaką ma posiadać element
 * @param {any} value - wartość jaką ma mieć wskazana właściwość
 * @returns {T[]} przefiltrowane elementy tablicy
 */
export const filterBy = <T>(arr: T[], prop: keyof T, value: any): T[] => {
    return arr.filter((item) => {
        return item[prop] === value;
    });
};

/**
 * Funkcja sprawdza, czy przekazany element jest funkcją.
 *
 * @param {unknown} el - element do sprawdzenia
 * @returns {el is (...args: any[]) => any} zwraca true, jeżeli sprawdzany element jest funkcją.
 */
export const isFunctionType = (el: unknown): el is (...args: any[]) => any => {
    return typeof el === 'function';
};
